import { isNotUndefined, moveToFulfilled, update } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import { CortexTopicsReceivedAction } from '../../domains/cortexTopics/actions.types';
import {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import { Feed, Thread, Topic } from '../types';

import { OfficeTopicsAwaitingName } from './types';

type FeedOfficeTopicsAwaitingNameAction =
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction;

type ThreadOfficeTopicsAwaitingNameAction =
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction;

export type OfficeTopicsAwaitingNameAction =
  | FeedOfficeTopicsAwaitingNameAction
  | ThreadOfficeTopicsAwaitingNameAction
  | CortexTopicsReceivedAction;

const initialState: OfficeTopicsAwaitingName = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const stateWithTopicsAwaitingName = (
  state: OfficeTopicsAwaitingName,
  topics: Record<string, Topic> | undefined,
  entityId: Thread['id'] | Feed['id']
) => {
  if (!topics) {
    return state;
  }

  const existingOfficeTopicIds = state.fulfilled.byId[entityId]?.officeTopicIds || [];
  const payloadOfficeTopicIds = Object.values(topics)
    .map((topic) => topic.officeTopicId)
    .filter(isNotUndefined);

  if (!existingOfficeTopicIds.length) {
    return moveToFulfilled(state, { [entityId]: { id: entityId, officeTopicIds: payloadOfficeTopicIds } });
  }

  const mergedOfficeTopics = [...existingOfficeTopicIds, ...payloadOfficeTopicIds];
  const uniqueOfficeTopics = [...new Set(mergedOfficeTopics)];

  return update(state, entityId, { officeTopicIds: uniqueOfficeTopics });
};

const removeOfficeTopicIds = (state: OfficeTopicsAwaitingName) => ({
  ...state,
  fulfilled: { byId: {} },
});

const reducer: Reducer<OfficeTopicsAwaitingName, OfficeTopicsAwaitingNameAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FEED.LOAD_FULFILLED':
      return stateWithTopicsAwaitingName(state, action.payload.entities.topics, action.payload.id);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return stateWithTopicsAwaitingName(state, action.payload.entities.topics, action.payload.id);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return stateWithTopicsAwaitingName(state, action.payload.entities.topics, action.payload.id);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return stateWithTopicsAwaitingName(state, action.payload.entities.topics, action.payload.threadId);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return stateWithTopicsAwaitingName(state, action.payload.entities.topics, action.payload.threadId);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return stateWithTopicsAwaitingName(state, action.payload.entities.topics, action.payload.threadId);
    case 'OFFICE_TOPICS.CORTEX_TOPICS_RECEIVED':
      return removeOfficeTopicIds(state);
    default:
      return state;
  }
};

export default reducer;
